import { FC, Suspense, useState } from 'react';

import { Link, router, useForm, usePage } from '@inertiajs/react';
import { Contact, Package, Truck } from 'lucide-react';
import { Clock, User } from 'lucide-react';

import { ConfirmCancelModal } from './ConfirmCancelModal';
import { ConfirmCancelModalState } from './ConfirmCancelModal/ConfirmCancelModal.types';
import { OrderEditDialog } from './OrderEditDialog';
import { OrderItemsList } from './OrderItemsList';
import { OrderReviewSummary } from './OrderReviewSummary';
import { OrderRevisionPanel } from './OrderRevisionPanel';
import { formatOrderAmount } from './orderRevisionUtils';
import { OrderServiceFeeStatus } from './OrderServiceFeeStatus';
import { getStatusNoticeTone } from './orderStatusNoticeTone';
import { Pagination } from './Pagination';
import { SalesAnalyticsRow } from './SalesAnalyticsRow';
import { SalesDeliveryInfo } from './SalesDeliveryInfo';
import { SalesStatusPanel } from './SalesStatusPanel';
import { SalesTrackingDialog } from './SalesTrackingDialog';
import { ServiceFeeBalanceStatus } from '../../../components/shared/serviceFeePresentation';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Routes, profileOrderActionRoute } from '../../../models/routes';
import { OrdersService } from '../../../services/OrdersService';
import { OrderCancellationReason, OfferProperty, Order } from '../../../types';
import { formatLocalDateTime } from '../../../utils';

type PageProps = {
    orders: Order[] | null;
    offerProperties: OfferProperty[];
    cancellationReasons: OrderCancellationReason[];
    nextPage: number | null;
    salesStats: {
        completedCount: number;
        completedTotalAmount: number;
    };
    serviceFeeBalanceStatus: ServiceFeeBalanceStatus;
};

const OrdersSkeleton: FC = () => {
    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {Array(3)
                    .fill(0)
                    .map((_, i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
            </div>
            <Skeleton className="h-12 w-full" />
            <div className="space-y-4">
                {Array(5)
                    .fill(0)
                    .map((_, i) => (
                        <Skeleton key={i} className="h-24 w-full" />
                    ))}
            </div>
        </div>
    );
};

const Sales: FC = () => {
    const {
        orders,
        offerProperties,
        cancellationReasons,
        nextPage,
        salesStats,
        serviceFeeBalanceStatus,
    } = usePage<PageProps>().props;
    const ordersList = orders || [];
    const [trackingModalOpen, setTrackingModalOpen] = useState(false);
    const [editOrder, setEditOrder] = useState<Order | null>(null);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [trackingError, setTrackingError] = useState('');
    const [trackingDialogMode, setTrackingDialogMode] = useState<
        'add' | 'edit'
    >('add');
    const [rejectModalState, setRejectModalState] =
        useState<ConfirmCancelModalState>({
            open: false,
            orderId: null,
            action: 'reject',
        });
    const {
        data: trackingData,
        setData: setTrackingData,
        patch: patchTracking,
        processing: trackingProcessing,
        reset: resetTrackingForm,
        transform: transformTracking,
    } = useForm<{ tracking_number: string }>({
        tracking_number: '',
    });

    const handleAddTracking = (order: Order) => {
        setSelectedOrderId(order.id);
        setTrackingData(
            'tracking_number',
            order.delivery?.tracking_number || '',
        );
        setTrackingDialogMode(
            order.status === 'shipped' || order.delivery?.tracking_number
                ? 'edit'
                : 'add',
        );
        setTrackingError('');
        setTrackingModalOpen(true);
    };

    const handleSubmitTracking = () => {
        if (!selectedOrderId) return;

        transformTracking((data) => ({
            tracking_number: data.tracking_number.trim().toUpperCase(),
        }));
        patchTracking(
            profileOrderActionRoute(selectedOrderId, 'add_tracking'),
            {
                preserveScroll: true,
                onSuccess: () => {
                    setTrackingModalOpen(false);
                    setSelectedOrderId(null);
                    setTrackingError('');
                    setTrackingDialogMode('add');
                    resetTrackingForm();
                },
                onError: (errors) => {
                    setTrackingError(
                        String(
                            errors.tracking_number ||
                                errors.general ||
                                'Не вдалося зберегти ТТН.',
                        ),
                    );
                },
            },
        );
    };

    const handleHandOver = (orderId: number) => {
        router.post(profileOrderActionRoute(orderId, 'hand_over'));
    };

    const handleConfirmOrder = (orderId: number) => {
        router.patch(profileOrderActionRoute(orderId, 'confirm_order'));
    };

    const handleReject = (orderId: number) => {
        setRejectModalState({
            open: true,
            orderId,
            action: 'reject',
        });
    };

    return (
        <div className="py-6">
            {rejectModalState.open && (
                <ConfirmCancelModal
                    state={rejectModalState}
                    setState={setRejectModalState}
                    reasons={cancellationReasons}
                />
            )}
            <OrderEditDialog
                order={editOrder}
                offerProperties={offerProperties}
                open={Boolean(editOrder)}
                onOpenChange={(open) => {
                    if (!open) setEditOrder(null);
                }}
            />

            <div className="mb-3 md:mb-6">
                <h1 className="text-2xl font-bold sm:text-3xl">Продажі</h1>
            </div>
            <SalesAnalyticsRow
                completedCount={salesStats.completedCount}
                completedTotalAmount={salesStats.completedTotalAmount}
            />

            <Suspense fallback={<OrdersSkeleton />}>
                {ordersList.length > 0 && (
                    <div className="mb-4 text-sm text-muted-foreground">
                        Показано {ordersList.length} замовлень
                    </div>
                )}

                {orders === null && (
                    <div className="mt-10 rounded-2xl border bg-muted/10 py-12 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                            <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="mb-1 text-lg font-medium">
                            Продажі не знайдено
                        </h3>
                        <p className="mb-4 text-sm text-muted-foreground">
                            У вас ще немає замовлень на ваші товари. Створіть
                            пропозицію, щоб почати продавати!
                        </p>
                        <Button asChild className="mt-4">
                            <Link href={Routes.NewOffer}>
                                <Package className="mr-2 h-4 w-4" />
                                Створити пропозицію
                            </Link>
                        </Button>
                    </div>
                )}

                {ordersList.length > 0 && (
                    <div className="mb-6 space-y-4">
                        {ordersList.map((order) => {
                            const statusNoticeTone = getStatusNoticeTone(order);

                            return (
                                <Card
                                    key={order.id}
                                    id={`order-${order.id}`}
                                    className="overflow-hidden transition-shadow hover:shadow-md"
                                >
                                    <CardContent className="p-0">
                                        <div className="flex items-start gap-4 p-4">
                                            <div className="mt-1 hidden flex-shrink-0 rounded-full bg-primary/10 p-2 md:block">
                                                <Package className="h-4 w-4 text-primary" />
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="mb-2 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <h3 className="text-sm font-semibold">
                                                            {order.number}
                                                        </h3>
                                                        <Badge
                                                            variant={OrdersService.getStatusBadgeVariant(
                                                                order.status,
                                                                order.delivery,
                                                            )}
                                                        >
                                                            {OrdersService.getStatusLabel(
                                                                order.status,
                                                                order.delivery,
                                                            )}
                                                        </Badge>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-semibold">
                                                            {formatOrderAmount(
                                                                order.total_price ||
                                                                    0,
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {
                                                                order
                                                                    .order_items
                                                                    ?.length
                                                            }{' '}
                                                            {order.order_items
                                                                ?.length === 1
                                                                ? 'товар'
                                                                : 'товари'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {formatLocalDateTime(
                                                            order.created_at,
                                                        )}
                                                    </div>
                                                    {order.user?.nickname && (
                                                        <div className="flex items-center gap-1">
                                                            <User className="h-3 w-3" />
                                                            Покупець:{' '}
                                                            <a
                                                                href={`/user/${order.user.nickname}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-semibold hover:underline"
                                                            >
                                                                {
                                                                    order.user
                                                                        .nickname
                                                                }
                                                            </a>
                                                        </div>
                                                    )}
                                                    {order.user
                                                        ?.telegram_username && (
                                                        <div className="flex items-center gap-1">
                                                            <Contact className="h-3 w-3" />
                                                            Telegram:{' '}
                                                            <a
                                                                href={`https://t.me/${order.user.telegram_username.replace('@', '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-semibold hover:underline"
                                                            >
                                                                {
                                                                    order.user
                                                                        .telegram_username
                                                                }
                                                            </a>
                                                        </div>
                                                    )}
                                                    {order.tracking_number && (
                                                        <div className="flex items-center gap-1">
                                                            <Truck className="h-3 w-3" />
                                                            Трек-номер:{' '}
                                                            {
                                                                order.tracking_number
                                                            }
                                                        </div>
                                                    )}
                                                </div>

                                                <SalesDeliveryInfo
                                                    order={order}
                                                />

                                                <OrderServiceFeeStatus
                                                    order={order}
                                                />

                                                <OrderItemsList
                                                    items={order.order_items}
                                                    label="Продані товари:"
                                                    offerProperties={
                                                        offerProperties
                                                    }
                                                />

                                                <OrderRevisionPanel
                                                    order={order}
                                                    role="seller"
                                                />

                                                <SalesStatusPanel
                                                    order={order}
                                                    tone={statusNoticeTone}
                                                    onAddTracking={
                                                        handleAddTracking
                                                    }
                                                    onHandOver={handleHandOver}
                                                    onConfirmOrder={
                                                        handleConfirmOrder
                                                    }
                                                    onReject={handleReject}
                                                    onEdit={setEditOrder}
                                                    serviceFeeStatus={
                                                        serviceFeeBalanceStatus
                                                    }
                                                />

                                                {order.review && (
                                                    <OrderReviewSummary
                                                        review={order.review}
                                                        title="Відгук покупця до цього замовлення"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {ordersList.length > 0 && (
                    <Pagination url={Routes.Sales} nextPage={nextPage} />
                )}
            </Suspense>

            <SalesTrackingDialog
                open={trackingModalOpen}
                onOpenChange={setTrackingModalOpen}
                mode={trackingDialogMode}
                trackingNumber={trackingData.tracking_number}
                onTrackingNumberChange={(value) => {
                    setTrackingData('tracking_number', value);
                    setTrackingError('');
                }}
                trackingError={trackingError}
                onSubmit={handleSubmitTracking}
                disabled={!selectedOrderId}
                processing={trackingProcessing}
            />
        </div>
    );
};

export default Sales;
